import clsx from 'clsx';
import React from 'react';
import { Button } from './Button';
import { IconCheck } from './icons';

/**
 * A multi-step form's progress, in two renderings of the same model, plus
 * its footer.
 *
 * - `Stepper` — compact header: "Step n of m · {title}" over a hairline
 *   track with m segments. Done and current segments are accent, the rest
 *   `line`. Use below `lg` and in dialogs.
 * - `StepNav` — vertical list for ≥ `lg`: number · label, the current step
 *   marked by a 2px accent bar, done steps by a check. Done steps are
 *   buttons (go back and edit); future steps are inert with a reason in
 *   `title` — never a bare disabled control.
 * - `StepFooter` — Back (secondary) · one primary (Continue / the final
 *   verb), with an optional left slot for a quiet note ("Draft saved").
 *
 * Steps can be conditional: pass only the steps that apply and the counts
 * follow. The model knows nothing about what a step contains.
 */
export interface StepItem<K extends string = string> {
  key: K;
  label: string;
  /** One line under the label in StepNav (current step only). */
  description?: string;
  /**
   * A quiet line under the label in StepNav that stays in every state —
   * where a step's answers come from ("From Main St"), or "Confirmed".
   */
  sub?: React.ReactNode;
}

export function Stepper<K extends string>({
  steps,
  current,
  className,
}: {
  steps: StepItem<K>[];
  current: K;
  className?: string;
}) {
  const idx = Math.max(
    0,
    steps.findIndex((s) => s.key === current),
  );
  const step = steps[idx];
  return (
    <div className={className}>
      <p className="v2-num text-v2-12 text-v2-fg-3">
        Step {idx + 1} of {steps.length}
      </p>
      <h2 className="mt-0.5 text-v2-16 font-semibold tracking-[-0.01em] text-v2-fg">
        {step?.label}
      </h2>
      <ol
        className="mt-3 flex gap-1"
        aria-label={`Step ${idx + 1} of ${steps.length}`}
      >
        {steps.map((s, i) => (
          <li
            key={s.key}
            aria-current={i === idx ? 'step' : undefined}
            aria-label={s.label}
            className={clsx(
              'h-0.5 flex-1 rounded-full',
              i <= idx ? 'bg-v2-accent' : 'bg-v2-line',
            )}
          />
        ))}
      </ol>
    </div>
  );
}

export function StepNav<K extends string>({
  steps,
  current,
  completed,
  onSelect,
  selectDisabledReason,
  className,
  'aria-label': ariaLabel = 'Steps',
}: {
  steps: StepItem<K>[];
  current: K;
  /**
   * Steps the user has finished. Defaults to every step before `current`;
   * pass it when the user can jump back from a review step.
   */
  completed?: readonly K[];
  onSelect: (key: K) => void;
  /** When set, finished steps stay put and the reason is their tooltip. */
  selectDisabledReason?: string;
  className?: string;
  'aria-label'?: string;
}) {
  const idx = Math.max(
    0,
    steps.findIndex((s) => s.key === current),
  );
  return (
    <nav aria-label={ariaLabel} className={className}>
      <ol className="flex flex-col">
        {steps.map((s, i) => {
          const done = completed ? completed.includes(s.key) : i < idx;
          const state = i === idx ? 'current' : done ? 'done' : 'todo';
          const inner = (
            <>
              <span
                className={clsx(
                  'v2-num inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-v2-11 font-medium',
                  state === 'current' && 'bg-v2-accent text-v2-on-accent',
                  state === 'done' && 'bg-v2-sunken text-v2-fg-2',
                  state === 'todo' && 'border border-v2-line text-v2-fg-4',
                )}
                aria-hidden
              >
                {state === 'done' ? <IconCheck size={11} /> : i + 1}
              </span>
              <span className="min-w-0">
                <span
                  className={clsx(
                    'block truncate leading-5',
                    state === 'current'
                      ? 'font-medium text-v2-fg'
                      : state === 'done'
                        ? 'text-v2-fg-2'
                        : 'text-v2-fg-4',
                  )}
                >
                  {s.label}
                </span>
                {(s.sub ?? (state === 'current' && s.description)) && (
                  <span className="block text-v2-12 leading-[18px] text-v2-fg-3">
                    {s.sub ?? s.description}
                  </span>
                )}
              </span>
            </>
          );
          const rowClass = clsx(
            'relative flex w-full items-start gap-2.5 rounded-v2 px-2 py-1.5 text-left text-v2-13',
            state === 'current' &&
              'before:absolute before:-left-2 before:top-1.5 before:h-5 before:w-0.5 before:rounded-full before:bg-v2-accent',
          );
          return (
            <li
              key={s.key}
              aria-current={state === 'current' ? 'step' : undefined}
            >
              {state === 'done' ? (
                <button
                  type="button"
                  onClick={() => onSelect(s.key)}
                  disabled={!!selectDisabledReason}
                  title={selectDisabledReason}
                  className={clsx(
                    rowClass,
                    selectDisabledReason
                      ? 'cursor-not-allowed'
                      : 'hover:bg-v2-sunken',
                  )}
                >
                  {inner}
                </button>
              ) : (
                <div
                  className={rowClass}
                  aria-disabled={state === 'todo' || undefined}
                  title={
                    state === 'todo'
                      ? 'Finish the current step first'
                      : undefined
                  }
                >
                  {inner}
                </div>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export const StepFooter: React.FC<{
  onBack?: () => void;
  backLabel?: string;
  onNext: () => void;
  nextLabel?: string;
  busy?: boolean;
  nextDisabledReason?: string;
  note?: React.ReactNode;
  className?: string;
}> = ({
  onBack,
  backLabel = 'Back',
  onNext,
  nextLabel = 'Continue',
  busy,
  nextDisabledReason,
  note,
  className,
}) => (
  <div
    className={clsx(
      'flex flex-col-reverse gap-3 border-t border-v2-line pt-5 sm:flex-row sm:items-center',
      className,
    )}
  >
    {note && <div className="text-v2-12 text-v2-fg-3 sm:mr-auto">{note}</div>}
    <div
      className={clsx(
        'flex flex-col-reverse gap-2 sm:flex-row sm:items-center',
        !note && 'sm:ml-auto',
      )}
    >
      {onBack && (
        <Button
          variant="secondary"
          onClick={onBack}
          disabled={busy}
          className="h-10 px-4 text-v2-14 sm:h-8 sm:px-3 sm:text-v2-13"
        >
          {backLabel}
        </Button>
      )}
      <Button
        variant="primary"
        onClick={onNext}
        loading={busy}
        disabledReason={nextDisabledReason}
        className="h-10 px-4 text-v2-14 sm:h-8 sm:px-3 sm:text-v2-13"
      >
        {nextLabel}
      </Button>
    </div>
  </div>
);
